<template>
  <div>
    <div class="toolbar">
      <el-input
        v-model="keyword"
        clearable
        placeholder="单号 / 材料"
        style="width:200px"
        @keyup.enter.native="load"
      />
      <el-select v-model="projectId" clearable filterable placeholder="项目" style="width:200px" @change="load">
        <el-option v-for="p in projects" :key="p.id" :label="projectLabel(p)" :value="p.id" />
      </el-select>
      <el-select v-model="supplierId" clearable filterable placeholder="供应商" style="width:180px" @change="load">
        <el-option v-for="s in suppliers" :key="s.id" :label="s.name" :value="s.id" />
      </el-select>
      <el-select v-model="status" clearable placeholder="审核状态" style="width:130px" @change="load">
        <el-option v-for="o in materialReturnStatusFilterOptions" :key="o.value" :label="o.label" :value="o.value" />
      </el-select>
      <el-button type="primary" icon="el-icon-search" @click="load">查询</el-button>
      <el-button icon="el-icon-download" :loading="exporting" :disabled="!list.length" @click="onExport">导出</el-button>
    </div>

    <el-table :data="pageList" border v-loading="loading" show-summary :summary-method="summaryMethod">
      <el-table-column prop="returnNo" label="退货单号" width="160" show-overflow-tooltip />
      <el-table-column prop="projectName" label="项目" min-width="140" show-overflow-tooltip>
        <template slot-scope="{ row }">
          <project-name-link :project-id="row.projectId" :name="row.projectName" />
        </template>
      </el-table-column>
      <el-table-column prop="projectAddress" label="项目地址" min-width="160" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.projectAddress || '-' }}</template>
      </el-table-column>
      <el-table-column prop="supplierName" label="供应商" min-width="130" show-overflow-tooltip />
      <el-table-column prop="materialName" label="材料" min-width="140" show-overflow-tooltip />
      <el-table-column prop="specs" label="规格" width="110" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.specs || '-' }}</template>
      </el-table-column>
      <el-table-column prop="unit" label="单位" width="70" align="center">
        <template slot-scope="{ row }">{{ row.unit || '-' }}</template>
      </el-table-column>
      <el-table-column prop="qty" label="退货数量" width="100" align="right">
        <template slot-scope="{ row }">{{ fmtQty(row.qty) }}</template>
      </el-table-column>
      <el-table-column label="审核状态" width="100" align="center">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="statusType(materialReturnStatusMap, row.status)">
            {{ statusLabel(materialReturnStatusMap, row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="remark" label="备注" min-width="120" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.remark || '-' }}</template>
      </el-table-column>
      <el-table-column prop="createdByName" label="创建人" width="80" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.createdByName || '-' }}</template>
      </el-table-column>
      <el-table-column prop="createdAt" label="创建时间" width="90">
        <template slot-scope="{ row }">{{ formatDateOnly(row.createdAt) }}</template>
      </el-table-column>
    </el-table>
    <list-pagination
      :total="listTotal"
      :page="page"
      :page-size="pageSize"
      @current-change="onPageChange"
      @size-change="onPageSizeChange"
    />
  </div>
</template>

<script>
import { listMaterialReturnLineRows, exportMaterialReturnLineRows, listSuppliers } from '../../api/supply'
import { listProjects } from '../../api/project'
import {
  materialReturnStatusMap, materialReturnStatusFilterOptions,
  statusLabel, statusType
} from '../../utils/status'
import ListPagination from '../../components/ListPagination.vue'
import ProjectNameLink from '../../components/ProjectNameLink.vue'
import listPaging from '../../mixins/listPaging'

export default {
  name: 'MaterialReturnLines',
  components: { ListPagination, ProjectNameLink },
  mixins: [listPaging],
  data() {
    return {
      materialReturnStatusMap,
      materialReturnStatusFilterOptions,
      keyword: '',
      projectId: null,
      supplierId: null,
      status: '',
      projects: [],
      suppliers: [],
      list: [],
      loading: false,
      exporting: false
    }
  },
  created() {
    this.loadFilters()
    this.load()
  },
  methods: {
    statusLabel,
    statusType,
    formatDateOnly(v) {
      if (!v) return '-'
      const s = String(v).replace('T', ' ')
      return s.length >= 10 ? s.slice(0, 10) : s
    },
    projectLabel(p) {
      if (!p) return ''
      return p.name || p.customerName || ('项目' + p.id)
    },
    num(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    },
    fmtQty(v) {
      if (v == null || v === '') return '0'
      const n = this.num(v)
      if (Number.isInteger(n)) return String(n)
      return String(Math.round(n * 10000) / 10000)
    },
    summaryMethod({ columns }) {
      const sums = []
      const rows = this.list || []
      columns.forEach((col, index) => {
        if (index === 0) {
          sums[index] = '合计'
          return
        }
        if (col.property === 'qty') {
          let total = 0
          rows.forEach(r => { total += this.num(r.qty) })
          sums[index] = this.fmtQty(total)
        } else {
          sums[index] = ''
        }
      })
      return sums
    },
    async loadFilters() {
      const [pr, sr] = await Promise.all([listProjects({}), listSuppliers({})])
      this.projects = pr.data || []
      this.suppliers = (sr.data || []).filter(s => s.enabled !== 0)
    },
    async load() {
      this.loading = true
      try {
        const res = await listMaterialReturnLineRows({
          keyword: this.keyword || undefined,
          projectId: this.projectId || undefined,
          supplierId: this.supplierId || undefined,
          status: this.status || undefined
        })
        this.list = res.data || []
        this.page = 1
      } finally {
        this.loading = false
      }
    },
    async onExport() {
      if (!this.list.length) {
        return this.$message.warning('暂无数据可导出')
      }
      this.exporting = true
      try {
        const res = await exportMaterialReturnLineRows({
          keyword: this.keyword || undefined,
          projectId: this.projectId || undefined,
          supplierId: this.supplierId || undefined,
          status: this.status || undefined
        })
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = '退货明细.xlsx'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (e) {
        this.$message.error('导出失败')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.toolbar { display:flex; gap:8px; margin-bottom:12px; flex-wrap:wrap; }
</style>
